














import {
  defineComponent, computed, onMounted, watch, ref,
} from '@vue/composition-api';
import type { PropType } from '@vue/composition-api';
import { PricingTypeEnum } from '@/enums/rights';
import { rightsManager } from './RightsManager';
import { viewRegistry } from './ViewRegistry';
import { registerAllViews } from './view-plugins';
import { DefaultInitStateEnum, RightsContext, TabTypeEnum } from './types';

export default defineComponent({
  name: 'RightCard',
  emits: ['tab-change'],
  props: {
    beforeTabChange: {
      type: Function as PropType<() => Promise<boolean>>,
      required: false,
    },
  },
  setup(props, { emit }) {
    // 注册所有视图（仅首次加载时执行）
    onMounted(() => {
      if (viewRegistry.getViewCount() === 0) {
        registerAllViews();
      }
    });

    // ✅ 是否显示权益卡片（先判断 shouldShow）
    const shouldShow = computed(() => rightsManager.shouldShowValue.value);

    // ✅ 自动响应式匹配视图 - 只有 shouldShow 为 true 时才匹配
    const viewResult = computed(() => {
      // 先判断是否应该显示
      if (!shouldShow.value) {
        return {
          config: {} as any,
          props: {},
          viewType: 'none',
          matched: false,
        };
      }

      // 直接访问 ref 的 value，computed 会自动追踪依赖
      const contextValue = rightsManager.context.value;
      return viewRegistry.match(contextValue as RightsContext);
    });

    // ===== 埋点处理 =====
    const trackingManager = rightsManager.getTrackingManager();
    // 标记是否已经上报过曝光埋点
    const hasReportedExpose = ref(false);

    // 监听权益数据变化，只在第一次 summaryData 发生变更时上报曝光埋点
    watch(
      () => rightsManager.context.value.rightSummaryData,
      (newVal, oldVal) => {
        // 只在第一次从无到有时上报，且卡片需要显示
        if (newVal && !oldVal && shouldShow.value && !hasReportedExpose.value) {
          const contextValue = rightsManager.context.value;
          trackingManager.trackCardExpose(contextValue);
          hasReportedExpose.value = true;
        }
      },
      { immediate: false },
    );

    /** 如果权益卡片从显示变为隐藏，且定价类型为模式五，则切换到普通通道 */
    watch(
      [
        shouldShow,
        () => rightsManager.context.value.planListData?.pricingType,
        () => rightsManager.context.value.initState,
      ],
      ([newShouldShow, newPricingType, newInitState]) => {
        if (!newShouldShow
        && newPricingType === PricingTypeEnum.LOAN_24_MUST_RIGHTS_OR_LOAN_36
        && newInitState === DefaultInitStateEnum.HAS_INIT
        ) {
          rightsManager.updateContext({
            showSpeed: false,
          }, false);
          emit('tab-change', TabTypeEnum.NORMAL);
        }
      },
      { immediate: true },
    );

    /** 按钮状态改变 */
    const handleTabChange = async (status: TabTypeEnum) => {
      // 上报Tab切换埋点
      const contextValue = rightsManager.context.value;
      const toSpeed = status === TabTypeEnum.SPEED;
      trackingManager.trackTabSwitch(contextValue, toSpeed);

      if (props.beforeTabChange && status === TabTypeEnum.NORMAL) {
        const result = await props.beforeTabChange();
        if (!result) {
          return;
        }
      }

      rightsManager.updateContext({
        showSpeed: status === TabTypeEnum.SPEED,
      }, false);
      // 获取权益概述数据
      // if (status === TabTypeEnum.SPEED) {
      //   await rightsManager.getSummaryData();
      // }
      emit('tab-change', status);
    };

    /** 勾选状态改变 */
    const handleCheckStatusChange = (status: boolean) => {
      const newCheckStatus = !status;

      // 上报协议勾选埋点
      if (newCheckStatus) {
        const contextValue = rightsManager.context.value;
        trackingManager.trackAgreementCheck(contextValue, newCheckStatus);
      }

      rightsManager.updateContext({
        checkStatus: newCheckStatus,
      }, false);
    };

    return {
      shouldShow,
      viewResult,
      handleTabChange,
      handleCheckStatusChange,
    };
  },
});
